import React, { useState, FC, ReactNode, ComponentType } from 'react'
import classnames from 'classnames'

import { styled, Theme, CSSObject } from '@mui/material/styles'
import MuiDrawer, { DrawerProps } from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'

import {
  MenuItem,
} from '../../types/app'

import icons from '../../icons'

import {
  TOPBAR_HEIGHT,
  DRAWER_WIDTH,
} from '../../settings'

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'auto',
})

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(64px + 1px)`,
})

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}))

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: DRAWER_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
)

export interface NavDrawerGetChildren {
  (onClick: (item: MenuItem) => void): ReactNode,
}

interface NavDrawerProps {
  getChildren: NavDrawerGetChildren,
  Icon?: ComponentType<React.PropsWithChildren<unknown>>,
  theme?: Record<string, string>,
}

const NavDrawer: FC<React.PropsWithChildren<NavDrawerProps>> = ({
  Icon,
  theme = {},
  getChildren,
}) => {
  const [open, setOpen] = React.useState(false)
  const openRef = React.useRef(false)

  const handleDrawerClose = () => {
      setOpen(false);
  }

  const handleDrawerToggle = () => {
      setOpen(!open);
  }

  const UseIcon = Icon || icons.menu

  return (
    <Drawer
      variant="permanent"
      open={ open }
      onClose={ handleDrawerClose }
      sx={{
        maxWidth: `${DRAWER_WIDTH}`,
        flexShrink: 0,
      }}
      PaperProps={{
        sx: {
          overflowX: 'ellipsis',
        }
      }}
    >
      <Box
        sx={{
          height: `${TOPBAR_HEIGHT}px`,
          mt: 0.5,
        }}
      />
      <Box
        sx={{
          height: `calc(100vh - (${TOPBAR_HEIGHT}px * 2))`,
          borderRadius: 0,
          maxWidth: `${DRAWER_WIDTH}px`,
        }}
        onMouseEnter={() => {
          if (!open) {
            setOpen(true)
            openRef.current = true
          }
        }}
        onMouseLeave={() => {
          if(openRef.current) {
            setOpen(false)
            openRef.current = false
          }
        }}
      >
        {
          getChildren(handleDrawerClose)
        }
      </Box>
      <DrawerHeader
        sx={{
          height: `${TOPBAR_HEIGHT}px`,
          mr: 1,
        }}
      >
        <IconButton onClick={handleDrawerToggle}>
          {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </DrawerHeader>
    </Drawer>
  )
}

export default NavDrawer
